import {
  QueryClient,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import * as jobApi from '@company/services/job/api';
import QueryKeys from '@constants/queryKeys';
import { formatShiftUpdateData } from '@company/utils/worker-modal-helper';
import { ShiftPosition } from '@th-types/job.detail.type';
import useAlert from '@hooks/useAlert';
import { ActivityDetailList } from '@th-types/daily-activity.type';
import {
  handleMissingActivityNoticed,
  handleMissingHoursNoticed,
} from '@company/services/job/api';

interface UpdateShiftParams {
  jobId: number;
  shiftId: number;
  shift: ShiftPosition;
  formData: {
    date: Date | null;
    positionId: number;
    positionName: string;
    wage: number;
    payRatePeriod: string;
    startTime: Date | null;
    endTime: Date | null;
    paidBreak: string;
  };
  jobTimezone?: string;
}

export function useUpdateShiftMutation() {
  const queryClient = useQueryClient();
  const { showSuccessAlert, showErrorAlert } = useAlert();

  return useMutation({
    mutationFn: async ({
      jobId,
      shiftId,
      shift,
      formData,
    }: UpdateShiftParams) => {
      const updateData = formatShiftUpdateData(shift, formData);
      const result = await jobApi.updateJobShifts(jobId, shiftId, updateData);

      // API returns 200 OK with error info in body, so we need to check success flag
      if (!result.success) {
        throw new Error(result.errorMessage || 'Failed to update shift');
      }

      return result;
    },
    onSuccess: (result) => {
      showSuccessAlert(result.message || 'Shift updated successfully');
      // Invalidate relevant queries
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.COMPANY_JOB_SHIFTS_LIST],
      });
      queryClient.invalidateQueries({ queryKey: [QueryKeys.WORKER_PROFILE] });
    },
    onError: () => {
      showErrorAlert('Failed to update shift');
    },
  });
}

type UseShiftNotificationsNoticedProps = {
  activitiesQueryKey: string[];
  queryClient: QueryClient;
};

export function useShiftMissingActivitiesNoticedMutation({
  queryClient,
  activitiesQueryKey,
}: UseShiftNotificationsNoticedProps) {
  return useMutation({
    mutationFn: (shiftPositionId: number) =>
      handleMissingActivityNoticed(shiftPositionId),
    onMutate: async (shiftPositionId: number) => {
      queryClient.setQueryData(
        activitiesQueryKey,
        (currentActivities: ActivityDetailList) => {
          return {
            ...currentActivities,
            activities: currentActivities.activities.map((activityItem) => {
              if (activityItem.shiftPosition.id === shiftPositionId) {
                return {
                  ...activityItem,
                  shiftPosition: {
                    ...activityItem.shiftPosition,
                    noticedMissingActivities:
                      !activityItem.shiftPosition.noticedMissingActivities,
                  },
                };
              }
              return activityItem;
            }),
          };
        }
      );
    },
  });
}

export function useActivityTrackerMissingHoursNoticedMutation({
  activitiesQueryKey,
  queryClient,
}: UseShiftNotificationsNoticedProps) {
  return useMutation({
    mutationFn: (shiftPositionId: number) =>
      handleMissingHoursNoticed(shiftPositionId),
    onMutate: async (shiftPositionId: number) => {
      queryClient.setQueryData(
        activitiesQueryKey,
        (currentActivities: ActivityDetailList) => {
          return {
            ...currentActivities,
            activities: currentActivities.activities.map((activityItem) => {
              if (activityItem.shiftPosition.id === shiftPositionId) {
                return {
                  ...activityItem,
                  shiftPosition: {
                    ...activityItem.shiftPosition,
                    noticedMissingHours:
                      !activityItem.shiftPosition.noticedMissingHours,
                  },
                };
              }
              return activityItem;
            }),
          };
        }
      );
    },
  });
}
